import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apis } from './apis';
import { Root } from './components/Root';

import {
  AlertDisplay,
  Header,
  OAuthRequestDialog,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { orgPlugin } from '@backstage/plugin-org';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { entityPage } from './components/catalog/EntityPage';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

import {
  EpDevPAppContextProvider,
  EpDevPNotFoundErrorPage,
  EpDevPRouter,
  EpDevPServiceUnavailablePage,
  EpDevPSimpleSupportPage,
} from '@solace-labs/backstage-plugin-ep-devp';
import {
  EpDevPPluginIds,
  EpDevPDocumentTypes,
  EpDevPSearchPageAssetFilters,
} from '@solace-labs/backstage-plugin-ep-devp-common';
import {
  OpenapiTabPage,
  RenderTabPage,
} from '@solace-labs/plugin-ep-devp-openapi';
import {
  EpDevPSearchPage,
  EpDevPSearchPageControl,
} from './components/ep-devp-search';
import { EpDevPConsumerProductsPage } from './components/Products/ep-devp-consumer-products';
import { EpDevPConsumerAppsPage } from './components/Application/plugin';
//  import { EpDevPListConsumerAppsPage } from './components/Application/EpDevPListConsumerAppsPage';
import { EpDevPManageAppsPage } from './components/ep-devp-manage-apps';
import {
  NavCanHomePage,
} from '@internal/plugin-navcan';

import { Button, CssBaseline, ThemeProvider } from '@material-ui/core';
import { NavCanDarkTheme, NavCanLightTheme } from './themes/navcan-theme';
import UsersFetchList from './components/users/UsersFetchList';
import { NotificationForm } from './components/notification/AddNotification';

import { SignInPage } from '@backstage/core-components';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import CustomSignInPage from './components/CustomSignInPage';
import NotificationsList from './components/notification/ViewNotification';
import { ContractForm } from './components/contract/AddContract';
import ContractsList from './components/contract/ViewContract';
import { EditContractForm } from './components/contract/EditContract';
import SubscriptionList from './components/Subscription/Subscription';
import { ProtectedRoute } from './components/ProtectedRoute';
import { QueueConsumersPage } from './components/QueueConsumers/QueueConsumersPage';
import QueueListPage from './components/QueueConsumers/QueueListPage';
import Dashboard from './components/Dashboard/Dashboard';
import { SessionHandler } from './SessionHandler';
import {ErrorBoundaryFallback} from './components/ErrorBoundary'
// import ErrorThrowingComponent from './components/error';
import CustomErrorBoundary from './components/CustomErrorBoundary';
import { UserProvider } from './UserContext';
import {
  ServiceRegistryLanguageProvider,
  ServiceRegistryPublicLanguageProvider,
  useServiceRegistryTranslation,
} from './i18n/ServiceRegistryLanguageProvider';
import { ProfileCompletionGuard } from './i18n/ProfileCompletionGuard';
import { ServiceRegistrySettingsPage } from './components/Settings/ServiceRegistrySettingsPage';
const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    NotFoundErrorPage: () => (
      <EpDevPNotFoundErrorPage status="404" statusMessage="PAGE NOT FOUND" />
    ),
    ErrorBoundaryFallback: ErrorBoundaryFallback,
    SignInPage: props => (
      <ServiceRegistryPublicLanguageProvider>
        <CustomSignInPage
          {...props}
          auto
          provider={{
            id: 'azure auth',
            title: 'azure',
            message: 'Sign in using azure',
            apiRef: microsoftAuthApiRef,
          }}
        />
      </ServiceRegistryPublicLanguageProvider>
    ),
  },
  themes: [
    {
      id: 'navcan-light',
      title: 'Light',
      variant: 'light',
      Provider: ({ children }) => (
        <ThemeProvider theme={NavCanLightTheme}>
          <CssBaseline>{children}</CssBaseline>
        </ThemeProvider>
      ),
    },
    {
      id: 'navcan-dark',
      title: 'Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <ThemeProvider theme={NavCanDarkTheme}>
          <CssBaseline>{children}</CssBaseline>
        </ThemeProvider>
      ),
    },
  ],
});

const DeveloperPortalSearchControl = () => {
  const { t } = useServiceRegistryTranslation();
  return (
    <EpDevPSearchPageControl
      key="/search/developer-portal"
      title={t('developerSearch.portalSearch')}
      documentTypes={[
        EpDevPDocumentTypes.EpDevPConsumerAppsDocumentType,
        EpDevPDocumentTypes.EpDevPConsumerProductsDocumentType,
        EpDevPDocumentTypes.EpDevPManageAppsDocumentType,
      ]}
      assetFilters={[
        EpDevPSearchPageAssetFilters.Domains,
        EpDevPSearchPageAssetFilters.Environments,
        EpDevPSearchPageAssetFilters.State,
        EpDevPSearchPageAssetFilters.BrokerType,
      ]}
    />
  );
};

const routes = (
  <SessionHandler>
    <CustomErrorBoundary>
  <ProfileCompletionGuard>
  <FlatRoutes>
    <Route path="/" element={<Navigate to="navcan-home" />} />

    <Route path="/navcan-home" element={<NavCanHomePage />} />

    <Route path="/dashboard" element={<Dashboard />} />
    {/* <Route path="/error-test" element={<ErrorThrowingComponent />} /> */}

    <Route
      path="/users-list"
      element={
        <ProtectedRoute allowedRoles={['Admin']}>
          <UsersFetchList />
        </ProtectedRoute>
      }
    />

    <Route
      path="/contract/add"
      element={
        <ProtectedRoute allowedRoles={['Contract Authority']}>
          <ContractForm />
        </ProtectedRoute>
      }
    />

    <Route path="/contract/view" element={<ContractsList />} />


    <Route
      path="/contract/edit/:contractId"
      element={
        <ProtectedRoute allowedRoles={['Contract Authority']}>
          <EditContractForm />
        </ProtectedRoute>
      }
    />

    <Route
      path="/notifications/add"
      element={
        <ProtectedRoute allowedRoles={['Admin']}>
          <NotificationForm />
        </ProtectedRoute>
      }
    />

    <Route path="/notifications/view" element={<NotificationsList />} />

    <Route path="/subscriptions/view" element={<SubscriptionList />} />

    <Route path="/settings" element={<Navigate to="/settings/general" replace />} />
    <Route path="/settings/general" element={<ServiceRegistrySettingsPage />} />

    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route path="/queue-consumers" element={
      <ProtectedRoute allowedRoles={['Admin']}>
      <QueueListPage/>
      </ProtectedRoute>} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>

    <Route path="/search/developer-portal" element={<EpDevPSearchPage />}>
      <DeveloperPortalSearchControl />
    </Route>

    <Route
      path={EpDevPPluginIds.EpDevPPluginId}
      element={
        <EpDevPRouter
          serviceUnavailablePage={() => <EpDevPServiceUnavailablePage />}
          supportPage={() => <EpDevPSimpleSupportPage />}
        />
      }
    />

    <Route
      path={EpDevPPluginIds.EpDevPConsumerProductsPluginId}
      element={<EpDevPConsumerProductsPage />}
    />

    <Route
      path={EpDevPPluginIds.EpDevPConsumerAppsPluginId}
      element={<EpDevPConsumerAppsPage />}
    />

    <Route
      path={`${EpDevPPluginIds.EpDevPManageAppsPluginId}`}
      element={
        <ProtectedRoute allowedRoles={['Admin']}>
          <EpDevPManageAppsPage />
        </ProtectedRoute>
      }
    />
    <Route path="/ep-devp-openapi" element={<OpenapiTabPage />} />
    <Route path="/ep-devp-openapi/openapi-page" element={<RenderTabPage />} />
  </FlatRoutes>
  </ProfileCompletionGuard>
  </CustomErrorBoundary>
  </SessionHandler>
);

export default app.createRoot(
  <>

    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
    <UserProvider>
      <ServiceRegistryLanguageProvider>
      <EpDevPAppContextProvider>
        <Root>{routes}</Root>
      </EpDevPAppContextProvider>
      </ServiceRegistryLanguageProvider>
      </UserProvider>
    </AppRouter>
  </>,
);
